import React, { useEffect, useState } from "react";
import {
  Modal,
  Space,
  Typography,
  Spin,
  Table,
  Tag,
  Tooltip,
  Button,
} from "antd";
import { TestRunService } from "../../services/TestRunService";
import { TestRunRequest } from "../../model_gen/evaluation";
import moment from "moment";
import {
  EyeOutlined,
  ArrowRightOutlined,
  DiffOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import RequestView from "../shared/RequestView";

const { Text } = Typography;

interface TestRunRequestComparisonProps {
  testRunId: number;
  testRunRequestId: number;
  isVisible: boolean;
  onClose: () => void;
  apiKey: string;
  testSetId?: string;
  systemPrompt?: string;
  intentGroupId?: string;
}

const TestRunRequestComparison: React.FC<TestRunRequestComparisonProps> = ({
  testRunId,
  testRunRequestId,
  isVisible,
  onClose,
  apiKey,
  testSetId,
  systemPrompt,
  intentGroupId,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [comparisonData, setComparisonData] = useState<TestRunRequest[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalRequests, setTotalRequests] = useState<number>(0);
  const [requestsByPage, setRequestsByPage] = useState<
    Record<string, TestRunRequest[]>
  >({});

  useEffect(() => {
    if (isVisible && testRunId && testRunRequestId) {
      const cacheKey = `${currentPage}-${pageSize}`;
      if (requestsByPage[cacheKey]) {
        setComparisonData(requestsByPage[cacheKey]);
      } else {
        fetchRequests(currentPage, pageSize);
      }
    }
  }, [isVisible, testRunId, testRunRequestId, currentPage, pageSize]);

  const fetchRequests = (page: number, size: number) => {
    const offset = (page - 1) * size;
    setIsLoading(true);
    TestRunService.getTestRunRequestComparison(
      testRunId,
      testRunRequestId,
      offset,
      size,
      setIsLoading,
      apiKey
    ).subscribe(
      (response: { items: TestRunRequest[]; total: number }) => {
        setComparisonData(response.items);
        setTotalRequests(response.total);
        const cacheKey = `${page}-${size}`;
        setRequestsByPage((prev) => ({
          ...prev,
          [cacheKey]: response.items,
        }));
        setIsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching comparison data:", error);
        setIsLoading(false);
      }
    );
  };

  const handlePageChange = (page: number, size?: number) => {
    if (size && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
      setRequestsByPage({}); // Clear cache when page size changes
    } else {
      setCurrentPage(page);
    }
  };

  const getCompareTooltip = (record: TestRunRequest) => {
    if (!record.original_request) return "No request found";

    return (
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "900px",
          maxWidth: "900px",
          boxSizing: "border-box",
        }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            marginTop: "12px",
          }}>
          <div>
            <strong style={{ marginLeft: "12px", marginBottom: "8px" }}>
              Original Response
              {record.original_request.metadata._maitai_test_request_response &&
                "*"}
            </strong>
            <div
              style={{
                padding: "12px",
                maxHeight: "400px",
                overflowY: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: "14px",
                lineHeight: "1.6",
              }}>
              {(record.original_request.metadata
                ._maitai_test_request_response as string) ||
                (record.original_request?.params &&
                  record.original_request.params.messages[
                    record.original_request.params.messages.length - 1
                  ]?.content) ||
                "No response"}
            </div>
          </div>
          <div>
            <strong style={{ marginLeft: "12px", marginBottom: "8px" }}>
              Test Response
            </strong>
            <div
              style={{
                padding: "12px",
                maxHeight: "400px",
                overflowY: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: "14px",
                lineHeight: "1.6",
              }}>
              {record.response?.choices?.[0]?.message?.content || "No response"}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: "Test Run",
      dataIndex: "test_run_id",
      key: "test_run_id",
      width: "10%",
      render: (text: number) => `#${text}`,
    },
    {
      title: "Date",
      dataIndex: "date_executed",
      key: "date_executed",
      width: "20%",
      render: (text: number) =>
        text ? moment(text).format("YYYY-MM-DD HH:mm:ss") : "-",
    },
    {
      title: "Score",
      key: "score",
      width: "15%",
      render: (_: unknown, record: TestRunRequest) => {
        const results = record.qualitative_evaluation_results || [];
        if (results.length === 0) return null;

        const maxPossibleScore = results.length * 5;
        const totalScore = results.reduce(
          (sum, result) => sum + (result.score_value || 0),
          0
        );
        const normalizedScore = Math.round(
          (totalScore / maxPossibleScore) * 100
        );

        const getScoreColor = (score: number) => {
          if (score >= 80) return "#52c41a";
          if (score >= 60) return "#faad14";
          return "#f5222d";
        };

        return (
          <Tooltip
            title={
              <div>
                {results.map((result, idx) => (
                  <div key={idx} style={{ marginBottom: "8px" }}>
                    {result.criteria}: {result.score_value}/5
                    {result.description && (
                      <div style={{ fontSize: "12px", color: "#ccc" }}>
                        {result.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            }>
            <Tag
              style={{
                color: getScoreColor(normalizedScore),
                borderColor: getScoreColor(normalizedScore),
                background: "transparent",
              }}>
              {normalizedScore}%
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "right" as const,
      render: (_: unknown, record: TestRunRequest) => (
        <Space style={{ justifyContent: "flex-end", width: "100%" }}>
          <Link
            to={`/application/${
              record.original_request?.application_id
            }/session/${encodeURIComponent(
              record.original_request?.session_id
            )}/request/${record.original_request?.request_id}`}
            target="_blank">
            <Tooltip
              title={
                <RequestView
                  request={record.original_request}
                  apiKey={apiKey}
                  testSetId={testSetId}
                  testSetRequestId={
                    record.original_request?.metadata
                      ?._maitai_test_request_id as string
                  }
                  systemPrompt={systemPrompt}
                />
              }
              placement="left"
              overlayStyle={{ maxWidth: "600px", width: "600px" }}>
              <Button type="link" icon={<EyeOutlined />}>
                Request
              </Button>
            </Tooltip>
          </Link>
          <Tooltip
            title={getCompareTooltip(record)}
            placement="left"
            overlayStyle={{ maxWidth: "900px", width: "900px" }}>
            <Button
              type="link"
              icon={<DiffOutlined />}
              onClick={(e) => e.stopPropagation()}>
              Responses
            </Button>
          </Tooltip>
          {intentGroupId && testSetId && (
            <Link
              to={`/intent-group/${intentGroupId}/testsets/${testSetId}/runs/${record.test_run_id}`}
              target="_blank">
              <Button type="link" icon={<ArrowRightOutlined />}>
                View Test Run
              </Button>
            </Link>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title="Test Run Request Comparison"
      open={isVisible}
      onCancel={onClose}
      footer={null}
      width={1200}>
      {isLoading ? (
        <div style={{ textAlign: "center", padding: "24px" }}>
          <Spin size="large" />
        </div>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Text type="secondary">
            Comparing responses across different test runs for the same test
            request
          </Text>
          <Table
            dataSource={comparisonData}
            columns={columns}
            rowKey={(record) => `${record.test_run_id}-${record.id}`}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalRequests,
              onChange: handlePageChange,
              showSizeChanger: true,
              pageSizeOptions: ["5", "10", "20", "50"],
            }}
            rowClassName={(record) => {
              return record.test_run_id === testRunId &&
                record.id === testRunRequestId
                ? "ant-table-row-selected"
                : "";
            }}
            onRow={(record) => ({
              style: {
                background:
                  record.test_run_id === testRunId &&
                  record.id === testRunRequestId
                    ? "#e6f7ff" // Light blue background for the selected row
                    : undefined,
              },
            })}
          />
        </Space>
      )}
    </Modal>
  );
};

export default TestRunRequestComparison;
